import { useMemo } from "react";
import { FileSpreadsheet, ChevronDown, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DropdownMenu } from "components/common/DropdownMenu";
import { useExportHandlers } from "./useExportHandlers";
import { TRIGGER_STYLE_BY_SIZE } from "./constants";
import type { DropdownMenuItem } from "components/common/DropdownMenu/types";
import type { ExportExcelButtonV3Props } from "./types";

export const ExportExcelButtonV3 = ({
  projectId,
  projectName,
  isFloorPlanGroupApproved,
  isCableGroupApproved,
  isQuantityGroupApproved,
  canManagePrices,
  cables,
  planRecords,
  boqExtraction,
  size = "sm",
  align,
  isDisabled = false,
}: ExportExcelButtonV3Props) => {
  const { t } = useTranslation();

  const {
    handleExportAggregated,
    handleExportPerPlan,
    handleExportCountList,
    handleExportFolderCountList,
    handleExportGaebX83,
    isExporting,
  } = useExportHandlers({
    projectId,
    projectName,
    isFloorPlanGroupApproved,
    isCableGroupApproved,
    isQuantityGroupApproved,
    canManagePrices,
    cables,
    planRecords,
    boqExtraction,
  });

  // Only a project with a filed active plan has a folder sheet to export
  const hasFolders = useMemo(
    () =>
      planRecords.some(
        (planRecord) =>
          planRecord.archivedAt === null && planRecord.folderId !== null,
      ),
    [planRecords],
  );

  const items = useMemo<DropdownMenuItem[]>(
    () => [
      {
        label: t("smartSurveyor.exportExcel.exportCountList"),
        onClick: handleExportCountList,
        disabled: isExporting,
      },
      ...(hasFolders
        ? [
            {
              label: t("smartSurveyor.exportExcel.exportFolderCountList"),
              onClick: handleExportFolderCountList,
              disabled: isExporting,
            },
          ]
        : []),
      {
        label: t("smartSurveyor.exportExcel.exportGaebX83"),
        onClick: handleExportGaebX83,
        disabled: isExporting,
      },
      {
        label: t("smartSurveyor.exportExcel.exportFullReport"),
        onClick: handleExportAggregated,
        disabled: isExporting,
      },
      {
        label: t("smartSurveyor.exportExcel.exportByPlan"),
        onClick: handleExportPerPlan,
        disabled: isExporting,
      },
    ],
    [
      t,
      hasFolders,
      handleExportAggregated,
      handleExportPerPlan,
      handleExportCountList,
      handleExportFolderCountList,
      handleExportGaebX83,
      isExporting,
    ],
  );

  const { className: triggerClassName, iconSize } = TRIGGER_STYLE_BY_SIZE[size];

  // The menu wraps its trigger in a button, so this is styled like one instead of being one
  return (
    <DropdownMenu
      items={items}
      className="flex-shrink-0"
      menuClassName="!min-w-48 !mt-1"
      align={align}
      disabled={isDisabled}
      trigger={
        <span className={triggerClassName}>
          {isExporting ? (
            <Loader2 size={iconSize} className="animate-spin" />
          ) : (
            <FileSpreadsheet size={iconSize} />
          )}
          {t("smartSurveyor.common.export")}
          <ChevronDown size={iconSize} />
        </span>
      }
    />
  );
};
